import { useMemo } from "react";
import { isIsoCurrency } from "@/lib/currencyTotals";

/**
 * Get the decimal separator for a given locale
 */
export const getDecimalSeparator = (locale: string): string => {
  const num = 1.1;
  return Intl.NumberFormat(locale).format(num).charAt(1);
};

/**
 * Get the thousands separator for a given locale
 */
export const getThousandsSeparator = (locale: string): string => {
  const num = 1000;
  return Intl.NumberFormat(locale).format(num).charAt(1);
};

/**
 * Parse a locale-formatted number string to a number
 * Handles both European (1.234,56) and US (1,234.56) formats
 */
export const parseLocaleNumber = (value: string, locale?: string): number => {
  if (!value || value.trim() === "") return 0;
  
  const cleanValue = value.trim();
  const hasComma = cleanValue.includes(",");
  const hasPeriod = cleanValue.includes(".");
  
  if (hasComma && hasPeriod) {
    // Check which comes last - that's the decimal separator
    const lastComma = cleanValue.lastIndexOf(",");
    const lastPeriod = cleanValue.lastIndexOf(".");
    if (lastComma > lastPeriod) {
      // European format: 1.234,56
      return parseFloat(cleanValue.replace(/\./g, "").replace(",", ".")) || 0;
    } else {
      // US format: 1,234.56
      return parseFloat(cleanValue.replace(/,/g, "")) || 0;
    }
  } else if (hasComma) {
    // Only comma - assume it's decimal separator (European format)
    return parseFloat(cleanValue.replace(",", ".")) || 0;
  }
  // Only period or no separator
  return parseFloat(cleanValue) || 0;
};

/**
 * Format a number according to locale with currency symbol.
 * Decimals follow the currency (2 for EUR, 0 for JPY) unless the caller sets them.
 * Crypto and other non-ISO codes show up to 8 decimals and the code after the
 * number, "0.00012 BTC": Intl rejects some (USDT) and rounds the rest to 2 (D-062).
 */
export const formatLocaleCurrency = (
  amount: number,
  locale: string,
  currency: string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showSymbol?: boolean;
  }
): string => {
  const { minimumFractionDigits, maximumFractionDigits, showSymbol = true } = options || {};

  try {
    if (!isIsoCurrency(currency)) {
      const number = new Intl.NumberFormat(locale, {
        minimumFractionDigits: minimumFractionDigits ?? 2,
        maximumFractionDigits: maximumFractionDigits ?? 8,
      }).format(amount);
      return showSymbol ? `${number} ${currency}` : number;
    }

    const currencyDefaults = new Intl.NumberFormat(locale, { style: "currency", currency }).resolvedOptions();
    const maxDigits = maximumFractionDigits ?? currencyDefaults.maximumFractionDigits;
    const digits = {
      minimumFractionDigits: Math.min(minimumFractionDigits ?? currencyDefaults.minimumFractionDigits, maxDigits),
      maximumFractionDigits: maxDigits,
    };
    return new Intl.NumberFormat(locale, showSymbol ? { style: "currency", currency, ...digits } : digits).format(amount);
  } catch {
    // Unknown locale: still never drop the currency.
    const number = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
    return showSymbol ? `${number} ${currency}` : number;
  }
};

/**
 * Format a number according to locale (without currency)
 */
export const formatLocaleNumber = (
  amount: number,
  locale: string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options || {};
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
};

/**
 * Validate if a string is a valid number in the given locale format
 */
export const isValidLocaleNumber = (value: string): boolean => {
  if (!value || value.trim() === "") return true; // Empty is valid (will be 0)
  
  const cleanValue = value.trim();
  
  // Allow digits, commas, periods, and leading minus
  const validPattern = /^-?[\d.,]+$/;
  if (!validPattern.test(cleanValue)) return false;
  
  // Try to parse and check if it's a valid number
  const parsed = parseLocaleNumber(cleanValue);
  return !isNaN(parsed) && isFinite(parsed);
};

/**
 * Get placeholder text based on locale
 */
export const getLocalePlaceholder = (locale: string, decimals: number = 2): string => {
  const separator = getDecimalSeparator(locale);
  const decimalPart = "0".repeat(decimals);
  return `0${separator}${decimalPart}`;
};

/**
 * Hook for locale-aware number formatting and parsing
 */
export const useLocale = (locale: string = "en-US") => {
  const decimalSeparator = useMemo(() => getDecimalSeparator(locale), [locale]);
  const thousandsSeparator = useMemo(() => getThousandsSeparator(locale), [locale]);
  
  const parseNumber = (value: string) => parseLocaleNumber(value, locale);
  
  const formatCurrency = (
    amount: number, 
    currency: string, 
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number; showSymbol?: boolean }
  ) => formatLocaleCurrency(amount, locale, currency, options);
  
  const formatNumber = (
    amount: number, 
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => formatLocaleNumber(amount, locale, options);
  
  const isValidNumber = (value: string) => isValidLocaleNumber(value);
  
  const getPlaceholder = (decimals?: number) => getLocalePlaceholder(locale, decimals);
  
  return {
    locale,
    decimalSeparator,
    thousandsSeparator,
    parseNumber,
    formatCurrency,
    formatNumber,
    isValidNumber,
    getPlaceholder,
  };
};
